/**
 * @fileoverview Activation-code verification mutation hook.
 * @description Wraps POST /identity/activation/mfa/verify in a TanStack mutation. Activation
 * issues no session, so on success this clears the pending challenge, leaves an "account
 * activated, sign in" notice for the login card, and navigates to /login.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import {
  verifyActivationCode,
  type VerifyActivationCodeRequest,
  type VerifyActivationCodeResponse,
} from '@/features/auth/services/activation.service'
import { markAccountActivatedNotice } from '@/features/auth/utils/password-changed-notice.utils'
import { ROUTES } from '@/shared/constants/routes.constants'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Submits the emailed activation code and, on success, sends the user to sign in.
 * @returns {ReturnType<typeof useMutation<VerifyActivationCodeResponse, ApiError,
 * VerifyActivationCodeRequest>>} The mutation object (mutate, isPending, error, ...).
 */
export function useVerifyActivationCode() {
  const { clearChallenge } = useTwoFactorChallenge()
  const navigate = useNavigate()

  return useMutation<VerifyActivationCodeResponse, ApiError, VerifyActivationCodeRequest>({
    mutationFn: verifyActivationCode,
    onSuccess: () => {
      markAccountActivatedNotice()
      clearChallenge()
      void navigate(ROUTES.LOGIN, { replace: true })
    },
  })
}
